/**
 * BrowserClient - Main orchestrator for browser-based IF games
 *
 * Wires together all managers (save, theme, dialog, menu, input, display)
 * and provides a simple API for story entry points.
 */

import type { GameEngine } from '@sharpee/engine';
import type { WorldModel } from '@sharpee/world-model';
import { TraitType, StoryInfoTrait } from '@sharpee/world-model';
import type { ISaveRestoreHooks, ISaveData, IRestartContext } from '@sharpee/core';
import type { ClientCapabilities, CmgtPacket, TurnPacket } from '@sharpee/if-domain';
import { Renderer, type IRenderer } from '@sharpee/channel-service';

import type {
  BrowserClientConfig,
  BrowserClientInterface,
  DOMElements,
  MenuHandlers,
  SaveContext,
} from './types';

import { ThemeManager } from './managers/ThemeManager';
import { SaveManager } from './managers/SaveManager';
import { DialogManager } from './managers/DialogManager';
import { MenuManager } from './managers/MenuManager';
import { InputManager } from './managers/InputManager';
import { TextDisplay } from './display/TextDisplay';
import { StatusLine } from './display/StatusLine';
import { AudioManager } from './audio/AudioManager';
import {
  registerDefaultBrowserRenderers,
  type BrowserDefaultLayout,
} from './channels';

/**
 * Default `ClientCapabilities` profile for the browser surface — full
 * graphical capabilities so every standard + media channel appears in
 * the per-client manifest. Authors override per `BrowserClientConfig.
 * clientCapabilities` for specialized surfaces (text-only kiosks, etc.).
 */
export const BROWSER_CAPABILITIES: ClientCapabilities = {
  text: true,
  images: true,
  animations: true,
  video: true,
  sound: true,
  music: true,
  speech: false,
  splitPane: true,
  statusBar: true,
  sidebar: true,
  clickableText: true,
  clickableImage: true,
  dragDrop: true,
  transitions: true,
  layers: true,
  customFonts: true,
};

export class BrowserClient implements BrowserClientInterface {
  // Configuration
  private config: BrowserClientConfig;

  // Managers
  private themeManager!: ThemeManager;
  private saveManager!: SaveManager;
  private dialogManager!: DialogManager;
  private menuManager!: MenuManager;
  private inputManager!: InputManager;
  private textDisplay!: TextDisplay;
  private statusLine!: StatusLine;
  private audioManager: AudioManager;

  // Engine integration
  private engine!: GameEngine;
  private world!: WorldModel;

  // Game state
  private currentTurn = 0;
  private currentScore = 0;
  private turnOffset = 0; // Offset to add to engine turn after restore
  /**
   * The `ISaveData` produced by the engine for the in-flight save
   * request. Set by `onSaveRequested` while the save dialog is open;
   * cleared in the hook's `finally`. Reused by `performSave` so the
   * persisted blob matches what the engine actually serialized for
   * this request, not whatever the world looked like by the time the
   * dialog returned.
   */
  private pendingEngineSave: ISaveData | null = null;

  // DOM elements reference
  private elements!: DOMElements;

  /**
   * ADR-165 channel renderer host. Constructed in `connectEngine()`
   * to drive the visible DOM via channel:manifest / channel:packet
   * (R5-C — primary rendering path).
   */
  private channelRenderer?: IRenderer;
  private channelLayout?: BrowserDefaultLayout;

  constructor(config: BrowserClientConfig) {
    this.config = {
      autoSave: true,
      ...config,
    };
    this.audioManager = new AudioManager();
  }

  /**
   * Initialize the browser client with DOM elements.
   * Call after DOMContentLoaded.
   */
  initialize(elements: DOMElements): void {
    this.elements = elements;

    // Set page title and menu title from story config
    document.title = this.config.storyInfo.title;
    const menuTitle = document.getElementById('menu-title');
    if (menuTitle) {
      menuTitle.textContent = this.config.storyInfo.title;
    }

    // Create text display and status line first (no dependencies)
    this.textDisplay = new TextDisplay({
      textContent: elements.textContent,
      mainWindow: elements.mainWindow,
    });

    this.statusLine = new StatusLine({
      statusLocation: elements.statusLocation,
      statusScore: elements.statusScore,
    });
  }

  /**
   * Connect to game engine and set up event handlers.
   * Call after creating the engine.
   */
  connectEngine(engine: GameEngine, world: WorldModel): void {
    this.engine = engine;
    this.world = world;

    // Now create managers that need world reference
    this.saveManager = new SaveManager({
      storagePrefix: this.config.storagePrefix,
      world: this.world,
      onStateChange: () => this.updateStatusLine(),
    });

    // Create dialog manager
    this.dialogManager = new DialogManager({
      elements: {
        saveDialog: this.elements.saveDialog,
        restoreDialog: this.elements.restoreDialog,
        startupDialog: this.elements.startupDialog,
        saveNameInput: this.elements.saveNameInput,
        saveSlotsListEl: this.elements.saveSlotsListEl,
        restoreSlotsListEl: this.elements.restoreSlotsListEl,
        noSavesMessage: this.elements.noSavesMessage,
        startupSaveInfo: this.elements.startupSaveInfo,
      },
      saveManager: this.saveManager,
      onDialogOpen: () => this.inputManager?.disable(),
      onDialogClose: () => {
        this.inputManager?.enable();
        this.inputManager?.focus();
      },
      generateSaveName: () => this.saveManager.generateSaveName(this.currentTurn),
      sanitizeSaveName: (name) => this.saveManager.sanitizeSaveName(name),
      performSave: (name) => this.performSave(name),
    });

    // Create theme manager
    this.themeManager = new ThemeManager({
      storageKey: `${this.config.storagePrefix}theme`,
      themes: this.config.themes,
      defaultTheme: this.config.defaultTheme,
    });

    // Create menu manager with handlers
    const menuHandlers: MenuHandlers = {
      onSave: () => this.handleSave(),
      onRestore: () => this.handleRestore(),
      onRestart: () => this.handleRestart(),
      onQuit: () => this.handleQuit(),
      onThemeSelect: (theme) => this.themeManager.applyTheme(theme),
      onHelp: () => this.engine.executeTurn('help'),
      onAbout: () => this.engine.executeTurn('about'),
    };

    this.menuManager = new MenuManager({
      menuBar: this.elements.menuBar,
      handlers: menuHandlers,
    });

    // Create input manager
    this.inputManager = new InputManager({
      commandInput: this.elements.commandInput,
      onCommand: (cmd) => this.executeCommand(cmd),
      isDialogOpen: () => this.dialogManager.isDialogOpen(),
    });

    // ADR-165 channel renderer — primary rendering path (R5-C).
    // After Phase 4 of channel-io-event-retirement, channel:manifest
    // and channel:packet are the only engine subscriptions. There is
    // no raw `engine.on('event', ...)` listener.
    this.setupChannelRenderer();

    // Set up all manager handlers
    this.dialogManager.setupHandlers();
    this.menuManager.setupHandlers();
    this.inputManager.setupHandlers();

    // Apply saved theme
    const savedTheme = this.themeManager.getSavedTheme();
    this.themeManager.applyTheme(savedTheme);

    // Sync existing saves to world
    this.saveManager.syncSavesToWorld();
  }

  /**
   * Append a platform-signal message to the main slot. Mirrors the
   * `mainChannelRenderer`'s DOM shape (`<p class="main-entry">` with
   * `pre-line` whitespace) plus a `system-message` class for theme
   * styling. Used for save/restore feedback strings that aren't
   * routed through the engine's text-service block production.
   *
   * Falls back to no-op if the channel layout hasn't been initialized
   * (engine not yet started).
   */
  private appendSystemMessage(text: string): void {
    const slot = this.channelLayout?.main;
    if (!slot) return;
    const doc = slot.ownerDocument;
    const p = doc.createElement('p');
    p.classList.add('main-entry', 'system-message');
    p.style.whiteSpace = 'pre-line';
    p.textContent = text;
    slot.appendChild(p);
    const win = this.elements?.mainWindow ?? slot.parentElement;
    if (win) win.scrollTop = win.scrollHeight;
  }

  /**
   * Build the ADR-165 channel renderer wired to the host page's
   * existing DOM elements (R5-C cutover).
   *
   * The host page provides three slots directly (`textContent`,
   * `statusLocation`, `statusScore`, `commandInput`). The remaining
   * slots needed by the platform-default renderers (notify, media,
   * meta, separate prompt label, separate turn element) are mounted
   * as hidden children of the existing main window.
   *
   * Score / turn override: the host page typically has a single
   * combined `Score: X | Turns: Y` element. After
   * `registerDefaultBrowserRenderers` we replace the platform-default
   * `score` and `turn` renderers with composites that update the
   * combined element. Stories that supply two separate elements get
   * the platform default by overriding back.
   *
   * Hotspot commands route through `engine.executeTurn` so UI
   * gestures synthesize typed-equivalent commands per ADR-163 §10.
   *
   * Auto-save migrates here from the legacy `text:output` listener:
   * `channel:packet` fires on every turn boundary (including idle
   * turns), so this is the single dependable signal.
   */
  private setupChannelRenderer(): void {
    const doc = document;
    const layout = this.adaptHostLayout();
    this.channelLayout = layout;
    this.channelRenderer = new Renderer({
      // Suppress JSON-tree fallback warnings — story channels may
      // legitimately register their renderers later (R7).
      fallbackWarn: () => undefined,
    });
    registerDefaultBrowserRenderers(this.channelRenderer, layout, {
      audio: this.audioManager,
      onMainAfterAppend: (slot) => {
        const win = this.elements?.mainWindow ?? slot.parentElement;
        if (win) win.scrollTop = win.scrollHeight;
      },
      onHotspotCommand: (command) => {
        this.engine.executeTurn(command);
      },
      lifecycle: {
        appendSystemMessage: (text) => this.appendSystemMessage(text),
        onRestoreCompleted: () => {
          // The standard `turn` channel's renderer already syncs
          // `currentTurn` on every packet, so `restore_completed`
          // only needs to refresh the combined status display.
          this.renderCombinedStatus();
        },
      },
    });

    // Prompt override — when the host page provides its own visible
    // prompt label (`.prompt` span etc.), only update that element.
    // The platform-default renderer ALSO sets `input.placeholder`,
    // which would render a second visible `> ` inside the empty
    // input field. Suppress the placeholder set when the label is
    // adopted from the host.
    if (
      this.channelLayout &&
      !this.channelLayout.inputPromptLabel.hidden &&
      this.channelLayout.inputPromptLabel.id !== 'sharpee-input-prompt'
    ) {
      const label = this.channelLayout.inputPromptLabel;
      this.channelRenderer.registerRenderer('prompt', {
        onValue: (value) => {
          if (typeof value !== 'string') return;
          label.textContent = value;
        },
      });
    }

    // Score+turn composite override — host page combines them in one
    // element. The override renderers maintain local caches so each
    // emission updates the combined string without losing the other
    // axis.
    const combined = this.elements?.statusScore ?? null;
    if (combined) {
      this.channelRenderer.registerRenderer('score', {
        onValue: (value) => {
          if (!value || typeof value !== 'object') return;
          const data = value as { current?: number; max?: number | null };
          if (typeof data.current === 'number') {
            this.currentScore = data.current;
          }
          this.renderCombinedStatus();
        },
      });
      this.channelRenderer.registerRenderer('turn', {
        onValue: (value) => {
          if (typeof value !== 'number') return;
          this.currentTurn = value + this.turnOffset;
          this.renderCombinedStatus();
        },
      });
    }

    const debugChannels = this.shouldDebugChannels();

    this.engine.on('channel:manifest', (cmgt: CmgtPacket) => {
      if (debugChannels) {
        // eslint-disable-next-line no-console
        console.log('[channel:manifest]', cmgt);
      }
      this.channelRenderer?.applyCmgt(cmgt);
    });
    this.engine.on('channel:packet', (packet: TurnPacket, turn: number) => {
      this.channelRenderer?.applyTurnPacket(packet);

      if (debugChannels) {
        // eslint-disable-next-line no-console
        console.log(`[channel:packet ${packet.turn_id}]`, packet.payload);
      }

      // Auto-save piggy-backs on the per-turn channel packet. ADR-163
      // §1: channel:packet fires every turn after text-service runs,
      // so it's the single dependable signal regardless of whether
      // any blocks were emitted that turn.
      if (this.config.autoSave && turn > 0) {
        const engineSave = this.engineCreateSave();
        this.saveManager.performAutoSave(engineSave, this.getSaveContext());
      }
    });
    void doc; // Suppresses unused-var lint when the doc reference is removed.
  }

  /**
   * Build a `BrowserDefaultLayout` from the existing host elements,
   * synthesizing hidden children for slots the host page doesn't
   * provide. Lets the platform-default renderers run against the
   * same DOM the legacy path used.
   */
  private adaptHostLayout(): BrowserDefaultLayout {
    const els = this.elements;
    const doc = document;
    const ensureHidden = (id: string, parent: HTMLElement, tag = 'div'): HTMLElement => {
      let el = doc.getElementById(id);
      if (!el) {
        el = doc.createElement(tag);
        el.id = id;
        el.hidden = true;
        parent.appendChild(el);
      }
      return el;
    };

    const root =
      els.mainWindow?.parentElement ?? els.textContent?.parentElement ?? doc.body;
    const mainWindow = els.mainWindow ?? doc.body;
    const main = els.textContent ?? ensureHidden('sharpee-main', mainWindow);
    const status = els.statusLocation?.parentElement ?? ensureHidden('sharpee-status', mainWindow);
    const statusLocation = els.statusLocation ?? ensureHidden('sharpee-status-location', status, 'span');
    const statusScore = els.statusScore ?? ensureHidden('sharpee-status-score', status, 'span');
    const statusTurn = ensureHidden('sharpee-status-turn', status, 'span');
    const sidebar = ensureHidden('sharpee-sidebar', mainWindow, 'aside');
    const inputContainer = els.commandInput?.parentElement ?? ensureHidden('sharpee-input', mainWindow);
    // Host pages commonly include a static `<span class="prompt">>` /
    // similar visible prompt label in the input row. Adopt it as the
    // prompt slot so the channel renderer updates the existing visible
    // element instead of a hidden synthetic span. Falls back to the
    // hidden synthesized span when the host doesn't supply one.
    const existingPromptLabel = inputContainer.querySelector(
      '.sharpee-input-prompt, [data-role="prompt"], #sharpee-input-prompt',
    ) as HTMLElement | null;
    const inputPromptLabel =
      existingPromptLabel ?? ensureHidden('sharpee-input-prompt', inputContainer, 'span');
    if (!inputPromptLabel.textContent) inputPromptLabel.textContent = '> ';
    const input = (els.commandInput ?? (() => {
      const i = doc.createElement('input');
      i.type = 'text';
      i.id = 'sharpee-input-field';
      i.hidden = true;
      inputContainer.appendChild(i);
      return i;
    })()) as HTMLInputElement;
    const media = ensureHidden('sharpee-media', mainWindow);
    if (!media.style.position) media.style.position = 'relative';
    const notify = ensureHidden('sharpee-notify', mainWindow);
    const meta = ensureHidden('sharpee-meta', mainWindow);

    return {
      root,
      status,
      statusLocation,
      statusScore,
      statusTurn,
      main,
      sidebar,
      input,
      inputPromptLabel,
      media,
      notify,
      meta,
    };
  }

  /**
   * True when channel-debug logging is enabled. Three opt-in paths
   * (any one is sufficient):
   *  1. `BrowserClientConfig.debugChannels: true`
   *  2. URL query string `?debug-channels=1` (or any truthy value)
   *  3. `localStorage['sharpee-debug-channels']` set to a truthy value
   *
   * Lets authors flip on/off without rebuilding — useful when
   * inspecting an installed bundle's per-turn channel emissions.
   */
  private shouldDebugChannels(): boolean {
    if (this.config.debugChannels) return true;
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('debug-channels');
      if (q && q !== '0' && q !== 'false') return true;
    } catch {
      // window.location not available — non-browser host.
    }
    try {
      const ls = window.localStorage?.getItem('sharpee-debug-channels');
      if (ls && ls !== '0' && ls !== 'false') return true;
    } catch {
      // localStorage blocked or unavailable.
    }
    return false;
  }

  /**
   * Render the combined `Score: X | Turns: Y` string into the host
   * page's combined status element. The host's traditional layout
   * uses one element; the score and turn channel renderers feed this
   * function rather than writing directly.
   */
  private renderCombinedStatus(): void {
    const el = this.elements?.statusScore;
    if (!el) return;
    el.textContent = `Score: ${this.currentScore} | Turns: ${this.currentTurn}`;
  }

  /**
   * Start the game (check for autosave, show initial look)
   */
  async start(): Promise<void> {
    console.log('=== BROWSER CLIENT START ===');

    try {
      // Set client version on StoryInfoTrait for banner display
      if (this.world) {
        const storyInfoEntities = this.world.findByTrait(TraitType.STORY_INFO);
        const trait = storyInfoEntities[0]?.get<StoryInfoTrait>(TraitType.STORY_INFO);
        if (trait) {
          trait.clientVersion = this.config.storyInfo.version;
        }
      }

      // Start the engine first (builds the world). ADR-163 channel
      // capabilities are passed so the engine constructs the
      // ChannelService for this surface before emitting the manifest.
      await this.engine.start({
        capabilities: this.config.clientCapabilities ?? BROWSER_CAPABILITIES,
      });

      // Sync localStorage saves to world
      this.saveManager.syncSavesToWorld();

      // Check for autosave and restore. The envelope carries the
      // engine's full `ISaveData`; applying it via the engine wipes
      // the freshly-initialized world and rebuilds from the snapshot,
      // restoring score / capabilities / state values / relationships
      // / ID counters that the v3 in-house serializer used to drop.
      const autosaveEnvelope = this.saveManager.loadAutosaveEnvelope();
      if (autosaveEnvelope) {
        console.log('[startup] Found autosave, restoring...');
        try {
          this.engineApplySave(autosaveEnvelope.engineSave);
          this.currentTurn = autosaveEnvelope.engineSave.metadata.turnCount;
          this.currentScore = autosaveEnvelope.score;
          // Engine's turn counter is now `currentTurn + 1` after the
          // engine restore; offset keeps the displayed counter aligned.
          this.turnOffset = this.currentTurn - 1;

          if (autosaveEnvelope.transcriptHtml) {
            const html = this.saveManager.decompressTranscript(
              autosaveEnvelope.transcriptHtml,
            );
            this.textDisplay.setHTML(html);
          }

          console.log('[startup] Restored to turn', this.currentTurn, 'score', this.currentScore, 'offset', this.turnOffset);
          this.updateStatusLine();

          // Show restored message
          this.textDisplay.displayText('[Session restored]');
          this.textDisplay.scrollToBottom();
          this.syncScoreFromWorld();
        } catch (error) {
          console.error('[startup] Failed to restore autosave:', error);
          // Fall through to new game
          await this.engine.executeTurn('look');
        }
      } else {
        // New game - execute initial look
        console.log('Executing initial look command...');
        await this.engine.executeTurn('look');
        console.log('Initial look complete');
      }

      // Focus input
      this.inputManager?.focus();
    } catch (error) {
      console.error('=== STARTUP ERROR ===', error);
      this.textDisplay.displayText(`[Startup Error: ${error}]`);
    }
  }

  /**
   * Execute a command
   */
  async executeCommand(command: string): Promise<void> {
    // Unlock audio on first user command (browsers block autoplay).
    // ADR-169: unlock() is async — must await so AudioContext.resume()
    // completes before any queued events fire into a still-suspended context.
    await this.audioManager.unlock();

    // Display command echo
    this.textDisplay.displayCommand(command);

    // Execute command
    try {
      await this.engine.executeTurn(command);
      // Sync score from world after each turn
      this.syncScoreFromWorld();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.textDisplay.displayText(`[Error: ${message}]`);
    }
  }

  /**
   * Get ISaveRestoreHooks for engine registration
   */
  getSaveRestoreHooks(): ISaveRestoreHooks {
    return {
      onSaveRequested: async (data: ISaveData): Promise<void> => {
        // Stash the engine's save data so the dialog handler
        // (`performSave`) uses *this* `ISaveData` rather than producing
        // a fresh one — the engine has already serialized for this
        // request. Cleared in the `finally` so a later menu-driven
        // save doesn't reuse stale state.
        this.pendingEngineSave = data;
        try {
          const saved = await this.dialogManager.showSaveDialog();
          if (!saved) {
            this.textDisplay.displayText('[Save cancelled]');
          }
        } finally {
          this.pendingEngineSave = null;
        }
      },

      onRestoreRequested: async (): Promise<ISaveData | null> => {
        // The dialog helper applies the engine save itself (preserves
        // the v3 timing where UI reads from the post-restore world),
        // so we always return null here — the engine emits
        // `restore_completed` regardless.
        await this.runRestoreDialog();
        return null;
      },

      onRestartRequested: async (_context: IRestartContext): Promise<boolean> => {
        if (confirm('Are you sure you want to restart? All unsaved progress will be lost.')) {
          // Clear autosave and reload
          this.saveManager.clearAutosave();
          window.location.reload();
          return true;
        }
        return false;
      },
    };
  }

  // === Menu handlers ===

  private async handleSave(): Promise<void> {
    await this.dialogManager.showSaveDialog();
  }

  private async handleRestore(): Promise<void> {
    await this.runRestoreDialog();
  }

  /**
   * Show the restore dialog, load the envelope, apply the engine save,
   * and update browser-side UI. Returns true on success, false if the
   * user cancelled or no save was found. Used by both the menu path
   * and the engine-event hook path so they share identical timing.
   *
   * The order is deliberate: the engine save is applied to the world
   * BEFORE any UI update so that {@link updateStatusLine} (which reads
   * the player's containing room from the world) sees the post-restore
   * state.
   */
  private async runRestoreDialog(): Promise<boolean> {
    const slotName = await this.dialogManager.showRestoreDialog();
    if (!slotName) {
      this.textDisplay.displayText('[Restore cancelled]');
      return false;
    }

    const envelope = this.saveManager.loadEnvelope(slotName);
    if (!envelope) {
      this.textDisplay.displayText(`[No saved game found: ${slotName}]`);
      return false;
    }

    // Apply the engine save first — this clears the live world and
    // rebuilds it from the snapshot. Without this, the v3 in-house
    // serializer's known gaps return (score / capabilities / world
    // state values / relationships / ID counters all silently
    // dropped). See docs/work/save-restore/...platform-save-restore-bug.md.
    this.engineApplySave(envelope.engineSave);

    this.currentTurn = envelope.engineSave.metadata.turnCount;
    this.currentScore = envelope.score;
    this.turnOffset = this.currentTurn - 1;

    if (envelope.transcriptHtml) {
      const html = this.saveManager.decompressTranscript(envelope.transcriptHtml);
      this.textDisplay.setHTML(html);
    }

    this.updateStatusLine();
    this.textDisplay.displayText(`[Game restored from "${slotName}"]`);
    this.syncScoreFromWorld();
    return true;
  }

  private async handleRestart(): Promise<void> {
    const hooks = this.getSaveRestoreHooks();
    await hooks.onRestartRequested?.({});
  }

  private handleQuit(): void {
    if (confirm('Are you sure you want to quit?')) {
      window.close();
      // If that doesn't work, show message
      this.textDisplay.displayText('[Close this browser tab to quit]');
    }
  }

  // === Public display methods (for story callbacks) ===

  displayText(text: string): void {
    this.textDisplay.displayText(text);
  }

  displayCommand(command: string): void {
    this.textDisplay.displayCommand(command);
  }

  clearScreen(): void {
    this.textDisplay.clearScreen();
  }

  // === Public getters (for story callbacks) ===

  getWorld(): WorldModel {
    return this.world;
  }

  getCurrentTurn(): number {
    return this.currentTurn;
  }

  getCurrentScore(): number {
    return this.currentScore;
  }

  /**
   * The ADR-165 channel renderer this client drives. Stories register
   * additional renderers (typically for story-defined channels like
   * `ambient:<id>` or custom event channels) by calling
   * `renderer.registerRenderer(...)` on the returned instance.
   *
   * Available after `connectEngine()` returns and before
   * `start()` is called — that window is when the default platform
   * renderers are registered but no `channel:packet` has fired yet.
   * Throws if called before `connectEngine()` (the renderer doesn't
   * exist yet).
   */
  getChannelRenderer(): IRenderer {
    if (!this.channelRenderer) {
      throw new Error(
        'BrowserClient.getChannelRenderer(): renderer not yet constructed. ' +
          'Call connectEngine(engine, world) first.',
      );
    }
    return this.channelRenderer;
  }

  /**
   * The shared `AudioManager` instance the platform-default audio
   * renderers (`sound`, `music`, `ambient:*`) delegate to. Stories
   * registering a custom `ambient:<id>` renderer pass this instance
   * to `createAmbientChannelRenderer(audio, id)` so playback shares
   * one Web Audio context.
   */
  getAudioManager(): AudioManager {
    return this.audioManager;
  }

  // === Private helpers ===

  /**
   * Call into the engine's save serializer. The engine produces a
   * complete `ISaveData` carrying the world's full runtime state via
   * the gzipped `worldSnapshot` field. Used by every save path
   * (interactive, autosave, startup).
   *
   * The cast bypasses the engine's `private` modifier on this method;
   * the platform-browser host needs both interactive (hook-driven) and
   * non-interactive (autosave / startup-restore) save paths, and the
   * public hook flow only covers the interactive case. Mirrors the
   * pattern used by the engine's own test suite.
   */
  private engineCreateSave(): ISaveData {
    return (
      this.engine as unknown as { createSaveData(): ISaveData }
    ).createSaveData();
  }

  /** Apply an engine save to the live world via `WorldModel.loadJSON`. */
  private engineApplySave(data: ISaveData): void {
    (
      this.engine as unknown as { loadSaveData(d: ISaveData): void }
    ).loadSaveData(data);
  }

  private performSave(slotName: string): void {
    // If we got here via an engine-fired `platform.save_requested`, the
    // hook stashed the engine's save data; reuse it so the persisted
    // state matches the moment the engine produced it. Otherwise (menu
    // File→Save), produce a fresh save now.
    const engineSave = this.pendingEngineSave ?? this.engineCreateSave();
    const result = this.saveManager.performSave(
      slotName,
      engineSave,
      this.getSaveContext(),
    );
    if (result.success) {
      this.textDisplay.displayText(`[Game saved as "${slotName}"]`);
    } else {
      this.textDisplay.displayText(`[Save failed: ${result.error}]`);
    }
  }

  private getSaveContext(): SaveContext {
    return {
      turnCount: this.currentTurn,
      score: this.currentScore,
      transcriptHtml: this.textDisplay.getHTML(),
    };
  }

  private updateStatusLine(): void {
    const location = this.saveManager.getCurrentLocation();
    this.statusLine.update(location, this.currentScore, this.currentTurn);
  }

  private syncScoreFromWorld(): void {
    const scoring = this.world.getCapability('scoring');
    if (scoring && typeof scoring.scoreValue === 'number') {
      const newScore = scoring.scoreValue;
      if (newScore !== this.currentScore) {
        this.currentScore = newScore;
        this.updateStatusLine();
      }
    }
  }

}
